import React from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../../context/LocalizationContext";
import { useUserData } from "../../hooks/useUserData";
import { useAuth } from "../../context/AuthContext";
import { useAppContext } from "../../context/AppContext";
import { useEndSession } from "../../hooks/useEndSession";
import { getGenderAgeDisplay as getGenderAgeDisplayHelper } from "../../utils/helpers";
import OptionGroup from "../../components/shared/OptionGroup";
import "./styles.css";
import voiceIcon from "../../assets/icons/newDesign/voice.svg";
import languageIcon from "../../assets/icons/newDesign/language.svg";
import endSessionIcon from "../../assets/icons/newDesign/endSession.svg";
import ProfileIcon from "../../assets/icons/profile.svg";
import Header from "../../containers/Header";
import CircularButton from "../../components/shared/CircularButton";
import { useWebSocket } from "../../context/WebSocketContext";

import EndSessionConfirmModal from "../../components/shared/EndSessionConfirmModal";

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { t, isRTL, language, setLanguage } = useLocalization();
  const { sendMessage } = useWebSocket();
  const { userData } = useUserData();
  const { isAuthenticated } = useAuth();
  const { aiLanguage, setAiLanguage } = useAppContext();
  const { showConfirm, isEnding, requestEndSession, confirmEndSession, cancelEndSession } = useEndSession();

  const handleAppLanguageChange = (newLang: string) => {
    setLanguage(newLang);
    sendMessage("language_selected", { language: newLang });
  };

  const getGenderAgeDisplay = (): string =>
    getGenderAgeDisplayHelper({
      gender: userData?.gender,
      age: userData?.age,
      labels: { female: t("female"), male: t("male"), years: t("years") },
      isRTL,
    });

  const handleAiLanguageChange = (newLanguage: string) => {
    setAiLanguage(newLanguage);
  };

  return (
    <div className="settings-container page-container scrollable-page">

      <Header
        title={t("settings.title")}
        statusText={ isAuthenticated ? t("settings.activeSession") : ""}
        showBackButton="RIGHT"
        onBack={() => navigate(-1)}
      />
      <div className="settings-content">
       {isAuthenticated && (
          <>
            <div className="settings-profile">
              <div className="profile-icon-container">
                <img src={ProfileIcon} alt="" className="profile-icon" aria-hidden="true" />
              </div>
              <div className="profile-details">
                <h2 className="profile-name page-text page-text--primary">{userData?.name ?? "—"}</h2>
                <p className="profile-info page-text">{getGenderAgeDisplay()}</p>
              </div>
            </div>
          </>
        )}

        {/* AI Assistant Language */}
        <OptionGroup
          title={t("settings.aiAssistant")}
          icon={voiceIcon}
          options={[
            { value: "en", label: t("language.english") },
            { value: "ar", label: t("language.arabic") },
          ]}
          value={aiLanguage}
          onChange={handleAiLanguageChange}
        />

        {/* App Language */}
        <OptionGroup
          title={t("settings.language")}
          icon={languageIcon}
          options={[
            { value: "en", label: t("language.english") },
            { value: "ar", label: t("language.arabic") },
          ]}
          value={language}
          onChange={handleAppLanguageChange}
        />

        {isAuthenticated && (
          <div className="end-session-wrapper">
            <CircularButton
              variant="danger"
              icon={endSessionIcon}
              label={''}
              onClick={requestEndSession}
              disabled={isEnding}
            />
          </div>
        )}
      </div>
      <EndSessionConfirmModal
        isOpen={showConfirm}
        onClose={cancelEndSession}
        onConfirm={confirmEndSession}
        isEnding={isEnding}
      />
    </div>
  );
};

export default Settings;
